import { useEffect, useRef, useState, type CSSProperties } from 'react';
import solMod from '../../levels/sol/obstacles/mod.png';
import {
  SOL_OVERLAY_FADE_MS,
  SOL_OVERLAY_HOLD_MS,
} from './gameplayConstants';
import './LevelSolOverlay.css';

type SolPhase = 'in' | 'hold' | 'out';

const PHASE_MS: Record<SolPhase, number> = {
  in: SOL_OVERLAY_FADE_MS,
  hold: SOL_OVERLAY_HOLD_MS,
  out: SOL_OVERLAY_FADE_MS,
};

const NEXT_PHASE: Record<SolPhase, SolPhase | null> = {
  in: 'hold',
  hold: 'out',
  out: null,
};

type LevelSolOverlayProps = {
  isPaused: boolean;
  onComplete: () => void;
};

export function LevelSolOverlay({
  isPaused,
  onComplete,
}: LevelSolOverlayProps) {
  const [phase, setPhase] = useState<SolPhase>('in');
  const [shown, setShown] = useState(false);
  const remainingMsRef = useRef(PHASE_MS.in);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setShown(true);
    });
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    if (phase === 'out') {
      setShown(false);
    }
  }, [phase]);

  useEffect(() => {
    if (isPaused) {
      return;
    }
    let frameId = 0;
    let lastNow = performance.now();
    const tick = (now: number) => {
      remainingMsRef.current -= now - lastNow;
      lastNow = now;
      if (remainingMsRef.current > 0) {
        frameId = window.requestAnimationFrame(tick);
        return;
      }
      const next = NEXT_PHASE[phase];
      if (next == null) {
        onCompleteRef.current();
        return;
      }
      remainingMsRef.current = PHASE_MS[next];
      setPhase(next);
    };
    frameId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [phase, isPaused]);

  return (
    <div
      className={
        shown ? 'level-sol-overlay level-sol-overlay--shown' : 'level-sol-overlay'
      }
      style={
        {
          transitionDuration: `${SOL_OVERLAY_FADE_MS}ms`,
        } as CSSProperties
      }
      aria-hidden
    >
      <img className="level-sol-overlay__mod" src={solMod} alt="" />
    </div>
  );
}
